import React, {useCallback, useMemo} from "react";
import "./index.scss";
import {connect} from "react-redux";
import {recommendationsDataStart} from "store/reducer/recommendation/data/reducer";
import {
    recommendationsDataErrorSelector,
    recommendationsDataLoadingSelector,
    recommendationsDataSelector
} from "store/reducer/recommendation/data/selectors";
import {LoadingLogoPortal} from "features/components/loading-logo";
import Text from "features/components/text";
import {__} from "services/i18n";
import Title from "features/components/title";
import {RecommendationItem} from "features/pages/profile/recommendation/item";
import moment, {ISO_8601} from "moment";
import {sendRecommendationSetShow} from "features/modals/send-recommendation/reducer";
import {ignoreRecommendationRequestStart} from "store/reducer/recommendation/ignore_request/reducer";
import Button from "features/components/button";
import {sendRecommendationShowModal} from "features/modals/send-recommendation/selectors";
import SendRecommendationModal from "features/modals/send-recommendation";
import {deleteRecommendationRequestStart} from "store/reducer/recommendation/delete_request/reducer";
import {deleteRecommendationStart} from "store/reducer/recommendation/delete/reducer";
import {acceptRecommendationStart} from "store/reducer/recommendation/accept/reducer";
import {setVisibilityRecommendationStart} from "store/reducer/recommendation/visibility/reducer";
import {withTranslation} from "react-i18next";

/* istanbul ignore next */
const mapStateToProps = state => ({
    data: recommendationsDataSelector(state),
    loading: recommendationsDataLoadingSelector(state),
    error: recommendationsDataErrorSelector(state),
    show_recommendation_modal: sendRecommendationShowModal(state),
});
/* istanbul ignore next */
const mapDispatchToProps = {
    getData: recommendationsDataStart,
    sendRecommendationSetShow,
    ignoreRecommendationRequestStart,
    deleteRecommendationRequestStart,
    deleteRecommendationStart,
    acceptRecommendationStart,
    setVisibilityRecommendationStart
};

export const ProfileRecommendations = (props) => {
    const {data, loading, error, show_recommendation_modal, t} = props;
    const {getData, sendRecommendationSetShow, ignoreRecommendationRequestStart, deleteRecommendationRequestStart, deleteRecommendationStart, acceptRecommendationStart, setVisibilityRecommendationStart} = props;
    const {requests_sent, requests_received, recommendation_sent, recommendation_received} = data;

    React.useEffect(() => {
        getData();
    }, [getData]);


    const openSendRecommendationModal = useCallback((request) => {
        sendRecommendationSetShow({show: true, id: request.id, name: request.sender.name});
    }, [sendRecommendationSetShow]);

    const ignoreRecommendationRequest = useCallback((request) => {
        ignoreRecommendationRequestStart({id: request.id});
    }, [ignoreRecommendationRequestStart]);

    const requestReceived = useMemo(() => {
        if(!requests_received || requests_received.length === 0) return null;
        return <div className={"section"}>
            <Title size={18}>{__(`profile-recommendations.requests_received.${requests_received.length === 1 ? "single" : "plural"}`, [{key: "[num]", value: requests_received.length}])}</Title>
            {requests_received.map(i => <RecommendationItem
                content={__("profile-recommendations.requests_received.text", [{key: "[date]", value: moment(i.created_at, ISO_8601).format("DD/MM/YYYY")}])}
                name={i.sender.name}
                avatar={i.sender.avatar}>
                <Button title={""} onClick={() => ignoreRecommendationRequest(i)} type={"square"} icon={"close"} />
                <Button title={""} className={"primary"} onClick={() => openSendRecommendationModal(i)} type={"square"} icon={"arrow-right"} />
            </RecommendationItem> )}
        </div>
    }, [ignoreRecommendationRequest, openSendRecommendationModal, requests_received]);

    const deleteRecommendationRequest = useCallback((request) => {
        deleteRecommendationRequestStart({id: request.id});
    }, [deleteRecommendationRequestStart]);


    const requestSent = useMemo(() => {
        if(!requests_sent || requests_sent.length === 0) return null;
        return <div className={"section"}>
            <Title size={18}>{__(`profile-recommendations.requests_sent.${requests_sent.length === 1 ? "single" : "plural"}`, [{key: "[num]", value: requests_sent.length}])}</Title>
            {requests_sent.map(i => <RecommendationItem
                content={__("profile-recommendations.requests_sent.text", [{key: "[date]", value: moment(i.created_at, ISO_8601).format("DD/MM/YYYY")}])}
                name={i.target.name}
                avatar={i.target.avatar}>
                <Button title={""} onClick={() => deleteRecommendationRequest(i)} type={"square"} icon={"delete"} status={"danger"} />
            </RecommendationItem> )}
        </div>
    }, [deleteRecommendationRequest, requests_sent]);

    const recommendationsSent = useMemo(() => {
        if(!recommendation_sent || recommendation_sent.length === 0) return null;
        return <div className={"section"}>
            <Title size={18}>{__(`profile-recommendations.recommendations_sent.${recommendation_sent.length === 1 ? "single" : "plural"}`, [{key: "[num]", value: recommendation_sent.length}])}</Title>
            {recommendation_sent.map(i => <RecommendationItem content={i.content} name={i.recommended.name} avatar={i.recommended.avatar} extra={moment(i.created_at, ISO_8601).format("MMMM YYYY")} /> )}
        </div>
    }, [recommendation_sent]);

    const not_approved_recommendations = useMemo(() => {
        return recommendation_received.filter(i => !i.accepted);
    }, [recommendation_received]);

    const recommendationsToApprove = useMemo(() => {
        if(!not_approved_recommendations || not_approved_recommendations.length === 0) return null;
        return <div className={"section"}>
            <Title size={18}>{__(`profile-recommendations.not_approved_recommendations.${not_approved_recommendations.length === 1 ? "single" : "plural"}`, [{key: "[num]", value: not_approved_recommendations.length}])}</Title>
            {not_approved_recommendations.map(i => <RecommendationItem
                content={i.content}
                name={i.adviser.name}
                avatar={i.adviser.avatar}
                extra={moment(i.created_at, ISO_8601).format("MMMM YYYY")}
            >
                <Button title={""} onClick={() => deleteRecommendationStart({id: i.id})} type={"square"} icon={"close"} />
                <Button title={""} className={"primary"} onClick={() => acceptRecommendationStart({id: i.id})} type={"square"} icon={"check"} />
            </RecommendationItem> )}
        </div>
    }, [not_approved_recommendations, acceptRecommendationStart, deleteRecommendationStart]);

    const approved_recommendations = useMemo(() => {
        return recommendation_received.filter(i => !!i.accepted);
    }, [recommendation_received]);

    const recommendationsApproved = useMemo(() => {
        if(!approved_recommendations || approved_recommendations.length === 0) return null;
        return <div className={"section"}>
            <Title size={18}>{__(`profile-recommendations.approved_recommendations.${approved_recommendations.length === 1 ? "single" : "plural"}`, [{key: "[num]", value: approved_recommendations.length}])}</Title>
            {approved_recommendations.map(i => <RecommendationItem
                content={i.content}
                name={i.adviser.name}
                avatar={i.adviser.avatar}
                extra={`${i.hidden ? t("profile-recommendations.hidden") : ""}${moment(i.created_at, ISO_8601).format("MMMM YYYY")}`}
            >
                <Button className={i.hidden ? "opaque" : ""} title={""} onClick={() => setVisibilityRecommendationStart({id: i.id, hidden: !i.hidden})} type={"square"} icon={"visibility-off"} />
            </RecommendationItem> )}
        </div>
    }, [approved_recommendations, setVisibilityRecommendationStart, t]);

    return <div className={"profile-recommendations"}>
        {error.inError && <Text size={16} align={"center"} color={"danger"}>{__(error.error)}</Text>}
        {loading && <LoadingLogoPortal />}
        {recommendationsApproved}
        {recommendationsToApprove}
        {requestSent}
        {requestReceived}
        {recommendationsSent}

        {!!show_recommendation_modal && <SendRecommendationModal />}
    </div>
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ProfileRecommendations));